import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Typography,
  Stack
} from "@mui/material";
import { Close, Save, Download, Info } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { TextButton } from "../../../components/commonComponents/Buttons/ActionButton";
import { HtmlTooltip, StyledDarkRedInformationTypography } from "../../../components/commonComponents/StyledComponents/styled";
import { IconContainer } from "../../../components/commonComponents/StyledComponents/styled";
import { useSelector, useDispatch } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import StoreDetailSelection from "../listings/StoreDetailSelection";
import StoreDetailFilter from "../filters/StoreDetailFilter";
import Pagination from "../../../components/commonComponents/LoadMore/Pagination";
import { loadStores } from "../../../api_calls/campaigns";
import { checkTodayDateWithFormat } from "../../../api_calls/utils";
import * as xlsx from "xlsx";

const DetailSearchContactBasedVisualStores = ({
  open,
  onClose,
  size = "lg",
  clipInfo,
  usedFor
}) => {
  const dispatch = useDispatch();
  const currentStateData = useSelector((state) => state.userSelections);

  const [selectedStores, setSelectedStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [sortedList, setSortedList] = useState([]);
  const [slicedFilteredStores, setSlicedFilteredStores] = useState([]);
  const [totalList, setTotalList] = useState([]);
  const [isLoadingStores, setIsLoadingStores] = useState(false);

  const allStores = currentStateData.stores_list || [];
  const filterd = currentStateData.filtered_stores || [];
  // This effect ensures that when the modal is opened, the selected stores are initialized based on the current state data. It also triggers the loading of stores if needed.
  const containerRef = React.useRef(null);

  // Determine if store selection should be disabled
  const statusBasedReadOnly =
    typeof currentStateData.new_clip_data !== "undefined" &&
    typeof currentStateData.new_clip_data.clip_start_end_status !== "undefined" &&
    ["Completed"].includes(currentStateData.new_clip_data.clip_start_end_status);

  useEffect(() => {
    setSelectedStores(clipInfo?.selected_store_options || []);
    const fetchStores = async () => {
      try {
        setIsLoadingStores(true);
        await loadStores();
      } finally {
        setIsLoadingStores(false);
      }
    };

    if (open) {
      fetchStores();
    }
  }, [open]);

  //Initialize selectedStores correctly for both add and update modes
  useEffect(() => {
    const filteredIds = new Set(filterd.map(s => s.id));
    const result = allStores.filter(store => filteredIds.has(store.id));
    setFilteredStores(result);
  }, [currentStateData.stores_list, currentStateData.filtered_stores, open]);

  const applyFilters = (filters) => {
    let result = [...allStores];

    if (filters.store_name?.length) {
      const ids = filters.store_name.map(s => s.id);
      result = result.filter(store => ids.includes(store.id));
    }

    if (filters.address?.length) {
      const addresses = filters.address.map(a => a.label);
      result = result.filter(store => addresses.includes(store.address));
    }

    if (filters.owner?.length) {
      const ownerIds = filters.owner.map(o => o.id);
      result = result.filter(store => ownerIds.includes(store.company.id));
    }

    if (typeof filters.listed !== "undefined" && filters.listed !== null) {
      const SelectedListed = filters.listed.value === "yes" ? true : false;
      result = result.filter(store => store.listing_check_saved === SelectedListed);
    }

    setFilteredStores(result);
  };

  //Fields for download Excel
  const downloadExcelFile = () => {
    const showListedColumn = typeof currentStateData.listing_check_saved_for_campaign !== "undefined" ? currentStateData.listing_check_saved_for_campaign : false;
    const data = showListedColumn === true ? [["Store Name", "Street & No.", "ZIP Code", "City", "Region", "Owner", "Listed"]] : [["Store Name", "Street & No.", "ZIP Code", "City", "Region", "Owner"]];

    sortedList.forEach((e) => {
      let dataToPush = [
        e.name,
        e.address,
        e.zipCode,
        e.city,
        e.region?.name ?? "",
        e.company?.name ?? ""
      ];
      // Add Listed column if applicable
      if (showListedColumn === true) {
        dataToPush = [
          ...dataToPush,
          e.listing_check_saved === true ? "Yes" : "No"
        ]
      }
      // 
      data.push(dataToPush);

    });

    const workbook = xlsx.utils.book_new();
    const worksheet = xlsx.utils.aoa_to_sheet(data);
    xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const currentDate = checkTodayDateWithFormat("YYYYMMDD");
    const exportFileName = clipInfo?.name
      ? `${currentDate}_${clipInfo.name.replaceAll(" ", "_")}_StoreOverview.xlsx`
      : `${currentDate}_NoClip_StoreOverview.xlsx`;

    xlsx.writeFile(workbook, exportFileName);
  };

  const handleApply = () => {
    dispatch(updateStateData({
      clip_stores: selectedStores.map(s => s.label),
      selected_store_options: selectedStores
    }));
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    // disableRestoreFocus prevents MUI's FocusTrap from returning focus to the trigger
    // element inside the parent dialog while that dialog still carries aria-hidden="true"
    <Dialog open={open} onClose={onClose} maxWidth={"xl"} fullWidth disableRestoreFocus>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6">
          Add Contact-based Visual: Detailed Selection
        </Typography>
      </DialogTitle>

      <DialogContent ref={containerRef}>
        <StoreDetailFilter onApplyFilters={applyFilters} open={open} />

        {typeof currentStateData.loading !== "undefined" && currentStateData.loading === true ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2">Loading stores</Typography>
          </Box>
        ) : (
          <StoreDetailSelection
            selectedStores={selectedStores}
            setSelectedStores={setSelectedStores}
            filteredStores={filteredStores}
            open={open}
            sortedList={sortedList}
            setSortedList={setSortedList}
            slicedFilteredStores={slicedFilteredStores}
            setSlicedFilteredStores={setSlicedFilteredStores}
            setTotalList={setTotalList}
            statusBasedReadOnly={statusBasedReadOnly}
          />
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          {(() => {
            const safeSliced = slicedFilteredStores ?? [];
            const safeSelected = selectedStores ?? [];

            const selectedInFiltered = safeSliced.filter(store =>
              safeSelected.some(sel => sel?.id === store?.id)
            ).length;

            return (
              <StyledDarkRedInformationTypography variant="body2">
                Selected {selectedInFiltered} of {safeSliced.length}
              </StyledDarkRedInformationTypography>
            );
          })()}

          {/* Only show download button when there are stores */}
          {sortedList.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={-1}>
              <TextButton
                onClick={(e) => {
                  e.preventDefault();
                  downloadExcelFile();
                }}
                label="Download Excel"
                icon={<Download />}
              />

              <HtmlTooltip
                title={
                  <Box sx={{ maxWidth: 200 }}>
                    <Typography variant="body2">
                      The export reflects the current state of data at the time of download.
                      Data in CAP Explorer may change over time.
                    </Typography>
                  </Box>
                }
              >
                <IconContainer>
                  <Info fontSize="small" sx={{ marginTop: 1 }} />
                </IconContainer>
              </HtmlTooltip>
            </Stack>
          )}
        </Stack>

        <Pagination
          totalCount={filteredStores.length}
          reduxPaginationKeyword="store_pagination"
          reduxStartKeyword="store_list_start"
          reduxEndKeyword="store_list_end"
          scrollContainerRef={containerRef}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2}>
          <CloseButton onClick={onClose} label="Cancel" icon={<Close />} />
          <ActionButton
            onClick={handleApply}
            label="Apply"
            icon={<Save />}
            disabled={!selectedStores.length}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DetailSearchContactBasedVisualStores;
